<script>
import { toRef, watchEffect } from 'vue';
import { useStore } from "vuex";
import { useState, useLog } from "../../../../../../services/hooks";
import {
  getExtensionByName,
  heicToBlobImage,
  isImage,
  isMimeTypePdf,
  megabytesToBytes,
  sizeFormatBytes,
  resizeImage } from "../../../../../../helpers/file";
import ObjectImageComponent from "./objects/ObjectImageComponent.vue";
import ObjectNameComponent from "./objects/ObjectNameComponent.vue";
import FilesCountComponent from "./objects/FilesCountComponent.vue";
import FilePromptComponent from "./objects/FilePromptComponent.vue";
import FileErrorComponent from "./objects/FileErrorComponent.vue";
import UploadFileButtonComponent from "./objects/UploadFileButtonComponent.vue";
import NavigationComponent from "./NavigationComponent.vue";
import DesktopFilesComponent from "./DesktopFilesComponent.vue";
import MobileFilesComponent from "./MobileFilesComponent";
import LoaderComponent from "../../../../../../components/LoaderComponent.vue";

export default {
  name: 'FilesComponent',
  components: {
    ObjectImageComponent,
    ObjectNameComponent,
    FilesCountComponent,
    FilePromptComponent,
    FileErrorComponent,
    UploadFileButtonComponent,
    NavigationComponent,

    DesktopFilesComponent,
    MobileFilesComponent,
    LoaderComponent
  },
  props: {
    objects: {
      type: Array,
      required: true
    },
    allowedExtensions: {
      type: Array,
      required: true
    },
    allowedMaxSumUploadSize: {
      type: Number,
      required: true
    },
    nextStepHandle: {
      type: Function,
      required: true
    }
  },
  setup(props) {

    const store = useStore();
    const objects = toRef(props, 'objects');
    const allowedExtensions = toRef(props, 'allowedExtensions');
    const allowedMaxSumUploadSize = toRef(props, 'allowedMaxSumUploadSize');
    const [isShowLoader, setIsShowLoader] = useState(false);
    const [loaderText, setLoaderText] = useState('');
    const { recordLog } = useLog();

    const getSummaryUploadedFilesSize = () => {
      let size = 0;
      objects.value.forEach(object => {
        object.files.forEach(file => {
          size += file.size;
        });
      });
      return size;
    };

    const isAllowExtension = (extension) => {
        return allowedExtensions.value.indexOf(extension) >= 0;
    };

    const onChangeFileHandler = async (e, objectKey) => {

      const uploadFiles = e.target.files || e.dataTransfer.files;

      if (!uploadFiles.length || uploadFiles.length === 0) {
        return;
      }

      const object = objects.value[objectKey];
      setIsShowLoader(true);

      for(let i=0; i<uploadFiles.length; i++) {

        setLoaderText(`Обработка файлов...${i} из ${uploadFiles.length}`);

        const uploadFile = uploadFiles[i];
        let file = {};
        file.name = uploadFile.name;

        if(!uploadFile.size) {
          object.filesError = 'Ошибка закачки файла ' + uploadFile.name + ', повторите';
          setIsShowLoader(false);
        } else {

          const extension = getExtensionByName(uploadFile.name);
          object.filesError = '';

          if (!isAllowExtension(extension)) {
            object.filesError = 'Неверный формат файла ' + uploadFile.name + '. Допустимые - ' + allowedExtensions.value.join(', ');
            setIsShowLoader(false);
            return;
          }

          if (isMimeTypePdf(uploadFile.type) && uploadFile.size > megabytesToBytes(20)) {
            object.filesError = 'Файл ' + uploadFile.name + ' превышает допустимый размер в 20мб';
            setIsShowLoader(false);
            return;
          }

          if((getSummaryUploadedFilesSize()+uploadFile.size) > allowedMaxSumUploadSize) {
            object.filesError = 'Превышен свободный лимит для загрузки файлов ('+sizeFormatBytes(allowedMaxSumUploadSize)+').';
            setIsShowLoader(false);
            return;
          }

          const fileInfo = { name: uploadFile.name, size: uploadFile.size, width: 0, height: 0 };

          if(isImage(extension)) {

            let f = uploadFile;

            if (extension === 'heic' || extension === 'heif') {
              f = await heicToBlobImage(f);
            }

            const resizedImage = await resizeImage(f);
            fileInfo.width = resizedImage.oldFile.width;
            fileInfo.height = resizedImage.oldFile.height;
            file.data = resizedImage.newFile.blob;

          } else {
            file.data = uploadFile;
          }

          object.files.push(file);
          object.filesInfo.push(fileInfo);
        }
      }

      setIsShowLoader(false);
    };

    const deleteFileHandler = (objectKey, fileKey) => {
      const object = objects.value[objectKey];
      object.files = object.files.filter((file, index) => index !== fileKey);
      object.filesInfo = object.filesInfo.filter((item, index) => index !== fileKey);
    };

    const hasUploadedFiles = () => {
      const items = objects.value;
      for (let i=0; i<items.length; i++) {
        const item = items[i];
        if(item.files.length===0) {
          return false;
        }
      }
      return true;
    };

    const showRequireFileErrorsHandler = (e) => {
      e.preventDefault();
      for (let i=0; i<objects.value.length; i++) {
        const object = objects.value[i];
        object.filesError = '';
        if(object.files.length===0) {
          object.filesError = 'Необходимо загрузить хотя бы один документ';
        }
      }
      return true;
    };

    watchEffect(() => {
      if (store.state.auth.user) {
        recordLog('Переход к загрузке документов');
      }
    });

    return {
      objects,
      deleteFileHandler,
      onChangeFileHandler,
      getExtensionByName,
      hasUploadedFiles,
      showRequireFileErrorsHandler,
      isShowLoader,
      loaderText
    }
  }
}
</script>

<template>
  <div class="step-1">
    <h1>Загрузка документов</h1>
    <div class="content">
      <div class="services">
        <div v-for="(object, objectKey) in objects" :key="objectKey" class="row">
          <div class="col-sm-2 left">
            <object-image-component :image-src="object.image" />
            <object-name-component :index="objectKey+1" :name="object.name" />
          </div>
          <div class="col-sm-10 right">
            <div class="table">
              <div class="tr">
                <div class="td">
                  <files-count-component
                    v-if="object.files.length"
                    :count-files="object.files.length"
                    :prompt="object.prompt"
                  />
                  <file-prompt-component
                    v-else-if="object.prompt"
                    :object-index="objectKey"
                    :prompt-mobile="object.promptMobile"
                    :prompt="object.prompt"
                  />
                  <template v-if="object.files.length">
                    <desktop-files-component
                      :object-index="objectKey"
                      :files="object.files"
                      :delete-file-handler="deleteFileHandler"
                    />
                    <mobile-files-component
                      :object-index="objectKey"
                      :files="object.files"
                      :delete-file-handler="deleteFileHandler"
                    />
                  </template>
                  <file-error-component :error="object.filesError" />
                  <upload-file-button-component
                    :object-index="objectKey"
                    :on-change-file-handler="onChangeFileHandler"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <navigation-component
      :show-require-file-errors-handler="showRequireFileErrorsHandler"
      :next-step-handler="nextStepHandle"
      :has-uploaded-file="hasUploadedFiles"
    />
    <loader-component v-if="isShowLoader" :text="loaderText"/>
  </div>
</template>

<style scoped>

  .services>.row {
    margin-bottom: 10px;
    padding-bottom: 5px;
    border-bottom: 1px solid #cccccc;
  }

  .services>.row:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }

  .services .col-sm-10 .table {
    height: 100%;
  }

  .services .col-sm-10 .td {
    vertical-align: middle;
  }

</style>
